import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  InputAdornment,
  styled,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  TablePagination,
  IconButton,
  Collapse,
  LinearProgress,
  TableSortLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { db, storage } from '../firebase';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  doc, 
  query, 
  where, 
  orderBy as firebaseOrderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  uploadBytesResumable
} from 'firebase/storage';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CustomAudioPlayer from './CustomAudioPlayer';
import config from '../config/environment';

// Styled Components
const MainContent = styled(Box)({
  padding: '0px 10px',
  backgroundColor: '#ffffff',
  maxWidth: '100%',
  overflow: 'auto'
});

const FormContainer = styled(Paper)({
  padding: '24px',
  marginBottom: '16px',
  backgroundColor: '#ffd999',
  borderRadius: '8px',
  width: '100%',
  '& .MuiBox-root': {
    width: '100%'
  }
});

const StyledTableContainer = styled(TableContainer)({
  marginTop: '16px',
  overflowX: 'auto',
  maxWidth: '100%',
  backgroundColor: '#fff',
  borderRadius: '4px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
  '@media (max-width: 1024px)': {
    maxWidth: '100vw',
    minWidth: '600px',
    padding: 0,
  },
  '& .MuiTable-root': {
    minWidth: '800px',
    '@media (max-width: 1024px)': {
      minWidth: '600px',
    },
  },
  '& .MuiTableCell-head': {
    backgroundColor: '#ffd999',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    padding: '8px 8px',
    border: '1px solid #e0e0e0',
    textAlign: 'center',
    fontSize: '0.85rem',
    '@media (max-width: 1024px)': {
      padding: '6px 6px',
      fontSize: '0.95rem',
    }
  },
  '& .MuiTableCell-body': {
    whiteSpace: 'nowrap',
    padding: '8px 8px',
    border: '1px solid #e0e0e0',
    fontSize: '12px',
    textAlign: 'center',
    '@media (max-width: 1024px)': {
      padding: '6px 6px',
      fontSize: '0.95rem',
    }
  },
  '& .status-cell': { 
    width: '80px', 
    maxWidth: '80px',
    '@media (max-width: 1024px)': {
      width: '100px',
      maxWidth: '100px',
    }
  },
  '& .name-cell': { 
    width: '300px', 
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    '@media (max-width: 1024px)': {
      width: '250px',
      maxWidth: '250px',
    },
    '&:hover': {
      overflow: 'visible',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      maxWidth: '300px',
    }
  },
  '& .pricing-cell': { 
    width: '100px', 
    maxWidth: '100px',
    '@media (max-width: 1024px)': {
      width: '120px',
      maxWidth: '120px',
    }
  },
  '& .image-cell': { 
    width: '100px', 
    minWidth: '80px',
    maxWidth: '120px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '4px',
    '@media (max-width: 1024px)': {
      width: '100px',
      minWidth: '80px',
      maxWidth: '120px',
      padding: '8px',
    }
  },
  '& .audio-cell': { 
    width: '80px', 
    maxWidth: '100px',
    padding: '4px',
    '@media (max-width: 1024px)': {
      width: '100px',
      maxWidth: '120px',
      padding: '8px',
    }
  },
  '& .actions-cell': { 
    width: '80px', 
    maxWidth: '100px',
    padding: '4px 8px',
    '@media (max-width: 1024px)': {
      width: '100px',
      maxWidth: '120px',
      padding: '8px 12px',
    }
  }
});

const UploadButton = styled(Button)({
  backgroundColor: '#2196F3',
  color: 'white',
  minWidth: '120px',
  height: '32px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#1976D2',
  },
  '@media (max-width: 1024px)': {
    minWidth: '140px',
    height: '40px',
    fontSize: '14px',
  }
});

const AddButton = styled(Button)({
  backgroundColor: '#2196F3',
  color: 'white',
  '&:hover': {
    backgroundColor: '#1976D2',
  },
  '@media (max-width: 1024px)': {
    minWidth: '140px',
    height: '40px',
    fontSize: '14px',
  }
});

const ActionButton = styled(Button)({
  textTransform: 'none',
  minWidth: 'auto',
  padding: '2px 4px',
  fontSize: '0.75rem',
  '@media (max-width: 1024px)': {
    padding: '4px 8px',
    fontSize: '0.875rem',
    minWidth: '80px',
  }
});

// Update TruncatedText to be more responsive
const TruncatedText = styled(Typography)({
  maxWidth: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'pointer',
  fontSize: '0.875rem',
  '@media (max-width: 960px)': {
    maxWidth: '150px',
  },
  '@media (max-width: 600px)': {
    maxWidth: '100px',
  }
});

// Add this styled component after other styled components
const SearchTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fff',
    borderRadius: '4px',
    height: '40px',
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: '#bdbdbd',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2196F3',
    },
    '@media (max-width: 1024px)': {
      height: '48px',
    }
  },
  '& .MuiOutlinedInput-input': {
    padding: '8px 14px',
    fontSize: '14px',
    '@media (max-width: 1024px)': {
      padding: '12px 16px',
      fontSize: '16px',
    }
  },
  '& .MuiInputAdornment-root': {
    '& .MuiSvgIcon-root': {
      fontSize: '24px',
      color: '#757575',
      '@media (max-width: 1024px)': {
        fontSize: '28px',
      }
    },
  },
});

// Add this constant at the top of the file after imports
const FIREBASE_STORAGE_BASE_URL = config.storage.baseURL;

interface CardData {
  id: string;
  product_id: number;
  name: string;
  description: string;
  is_free: boolean;
  price: number;
  image_url: string;
  thumbnail_url?: string;
  audio_url: string;
  created_at: Timestamp;
  updated_at: Timestamp;
  is_active: boolean;
}

interface UploadState {
  audio: { 
    file: File | null; 
    progress: boolean; 
    success: boolean; 
    error: string | null; 
    fileName: string;
    url: string;
    percent?: number;
  };
  image: { 
    file: File | null; 
    progress: boolean; 
    success: boolean; 
    error: string | null; 
    fileName: string;
    url: string;
    thumbnailUrl?: string;
  };
}

interface FormErrors {
  cardName: string;
  cardDescription: string;
  price: string;
  image_url: string;
  audio_url: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info';
}

const HighlightedRow = styled(TableRow)({
  animation: 'highlight 5s ease-out',
  '@keyframes highlight': {
    '0%': {
      backgroundColor: '#fff3e0',
      transform: 'scale(1.02)',
      color: '#d32f2f',
    },
    '50%': {
      backgroundColor: '#fff3e0',
      transform: 'scale(1.02)',
      color: '#d32f2f',
    },
    '100%': {
      backgroundColor: 'transparent',
      transform: 'scale(1)',
      color: 'inherit',
    },
  },
  '& td': {
    padding: '12px 16px',
    border: '1px solid #e0e0e0',
    fontSize: '12px',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    '& .MuiTypography-root': {
      color: 'inherit',
    },
    '& .MuiButton-root': {
      color: 'inherit',
    }
  }
});

// Utility to normalize card names for comparison
function normalizeCardName(name: string) {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

// Add these types near the top of the file
type Order = 'asc' | 'desc';
type SortField = 'name' | 'pricing' | 'updated_at';

export default function YantraCardManagement() {
  const [cardName, setCardName] = useState('');
  const [cardDescription, setCardDescription] = useState('');
  const [pricingType, setPricingType] = useState('Free');
  const [price, setPrice] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [cards, setCards] = useState<CardData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paidCardsCount, setPaidCardsCount] = useState(0);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [editMode, setEditMode] = useState(false);
  const [editCardId, setEditCardId] = useState('');
  const [uploadState, setUploadState] = useState<UploadState>({
    audio: { 
      file: null, 
      progress: false, 
      success: false, 
      error: null, 
      fileName: '',
      url: ''
    },
    image: { 
      file: null, 
      progress: false, 
      success: false, 
      error: null, 
      fileName: '',
      url: ''
    }
  });
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [cardToToggle, setCardToToggle] = useState<{ id: string; currentStatus: boolean } | null>(null);
  const [sortField, setSortField] = useState<string>('updated_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({
    cardName: '',
    cardDescription: '',
    price: '',
    image_url: '',
    audio_url: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState<string | null>(null);
  const [highlightedCardId, setHighlightedCardId] = useState<string | null>(null);
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement }>({});
  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [formAudioPlaying, setFormAudioPlaying] = useState(false);
  const [orderBy, setOrderBy] = useState<SortField>('updated_at');
  const [order, setOrder] = useState<Order>('desc');
  const [originalCardData, setOriginalCardData] = useState<CardData | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const soundInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const cardNameRef = useRef<HTMLInputElement>(null);
  const cardDescriptionRef = useRef<HTMLInputElement>(null);
  const priceRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // Function to check if there are any changes
  const checkForChanges = () => {
    if (!originalCardData || !editMode) {
      setHasChanges(false);
      return;
    }

    const currentPrice = pricingType === 'Paid' ? parseFloat(price) : 0;
    const originalPrice = originalCardData.is_free ? 0 : originalCardData.price;

    const changes = [
      cardName !== originalCardData.name,
      cardDescription !== originalCardData.description,
      pricingType !== (originalCardData.is_free ? 'Free' : 'Paid'),
      currentPrice !== originalPrice,
      uploadState.image.file !== null, // New image file selected
      uploadState.audio.file !== null   // New audio file selected
    ];

    setHasChanges(changes.some(change => change));
  };

  // Fetch cards on component mount
  useEffect(() => {
    fetchCards();
  }, []);

  // Add effect to clear highlighted card after animation
  useEffect(() => {
    if (highlightedCardId) {
      const timer = setTimeout(() => {
        setHighlightedCardId(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [highlightedCardId]);

  // Check for changes whenever form data changes
  useEffect(() => {
    checkForChanges();
  }, [cardName, cardDescription, pricingType, price, uploadState.image.file, uploadState.audio.file]);

  const fetchCards = async () => {
    try {
      setLoading(true);
      const cardsRef = collection(db, 'cards');
      const q = query(
        cardsRef,
        firebaseOrderBy('product_id', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const cardsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          product_id: data.product_id || 0,
          is_active: data.is_active,
          name: data.name,
          description: data.description,
          image_url: data.image_url,
          thumbnail_url: data.thumbnail_url,
          audio_url: data.audio_url,
          is_free: data.is_free,
          price: data.price || 0,
          created_at: data.created_at instanceof Timestamp ? data.created_at : Timestamp.fromDate(new Date(data.created_at)),
          updated_at: data.updated_at instanceof Timestamp ? data.updated_at : Timestamp.fromDate(new Date(data.updated_at))
        } as CardData;
      });
      setCards(cardsData);
    } catch (error) {
      console.error('Error fetching cards:', error);
      showSnackbar('Error fetching cards', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getNextProductId = async (): Promise<number> => {
    try {
      const cardsRef = collection(db, 'cards');
      const q = query(cardsRef, firebaseOrderBy('product_id', 'desc'), limit(1));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return 1; // If no cards exist, start with 1
      }
      
      const lastCard = querySnapshot.docs[0].data();
      return (lastCard.product_id || 0) + 1;
    } catch (error) {
      console.error('Error getting next product ID:', error);
      throw error;
    }
  };

  const compressImage = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            throw new Error('Failed to get canvas context');
          }

          // Calculate dimensions while maintaining aspect ratio
          let width = img.width;
          let height = img.height;
          const maxDimension = 1200; // Maximum dimension for uploaded image
          
          if (width > height) {
            if (width > maxDimension) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            }
          } else {
            if (height > maxDimension) {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;

          // Draw image with high-quality settings
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with quality settings
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Create a new file from the blob
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            'image/jpeg',
            0.8 // Quality setting (0.8 = 80% quality)
          );

          URL.revokeObjectURL(img.src);
        } catch (error) {
          URL.revokeObjectURL(img.src);
          reject(error);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Failed to load image for compression'));
      };
    });
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'audio' | 'image'
  ) => {
    try {
      console.log(`File input change triggered for ${type}`);
      const file = event.target.files?.[0];
      console.log('Selected file:', file);
      
      if (file) {
        // Validate file type
        const validAudioTypes = ['audio/mp3', 'audio/wav', 'audio/m4a', 'audio/ogg', 'audio/mpeg'];
        const validImageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml', 'image/gif'];
        
        if (type === 'audio' && !validAudioTypes.includes(file.type)) {
          // Check file extension as fallback
          const fileExtension = file.name.split('.').pop()?.toLowerCase();
          if (!['mp3', 'wav', 'm4a', 'ogg'].includes(fileExtension || '')) {
            setUploadState(prev => ({
              ...prev,
              [type]: { 
                ...prev[type],
                error: 'Invalid audio file format. Supported formats: MP3, WAV, M4A, OGG',
                success: false
              }
            }));
            showSnackbar('Invalid audio file format', 'error');
            return;
          }
        }

        if (type === 'image' && !validImageTypes.includes(file.type)) {
          setUploadState(prev => ({
            ...prev,
            [type]: { 
              ...prev[type],
              error: 'Invalid image file format. Supported formats: JPEG, PNG, WEBP, SVG, GIF',
              success: false
            }
          }));
          showSnackbar('Invalid image file format', 'error');
          return;
        }

        // Clear validation errors for correct file types
        setFormErrors(prev => ({
          ...prev,
          [type === 'audio' ? 'audio_url' : 'image_url']: ''
        }));

        // Validate file integrity
        try {
          if (type === 'audio') {
            const audio = new Audio();
            audio.src = URL.createObjectURL(file);
            await new Promise((resolve, reject) => {
              audio.oncanplaythrough = resolve;
              audio.onerror = reject;
            });
            URL.revokeObjectURL(audio.src);
          } else {
            const img = new Image();
            img.src = URL.createObjectURL(file);
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
            });
            URL.revokeObjectURL(img.src);
          }
        } catch (error) {
          setUploadState(prev => ({
            ...prev,
            [type]: { 
              ...prev[type],
              error: `Corrupt ${type} file. Please try another file.`,
              success: false
            }
          }));
          showSnackbar(`Corrupt ${type} file`, 'error');
          return;
        }

        // Determine the filename to use (trim if over 50 chars)
        let displayAndStorageFileName = file.name;
        let fileToUpload = file;
        if (file.name.length > 50) {
          try {
            displayAndStorageFileName = file.name.substring(0, 50);
            // Create a new File object with the trimmed name
            fileToUpload = new File([file], displayAndStorageFileName, { type: file.type });
          } catch (err) {
            setUploadState(prev => ({
              ...prev,
              [type]: { 
                ...prev[type],
                error: 'Your browser does not support renaming files for upload.',
                success: false
              }
            }));
            showSnackbar('File name too long and cannot be trimmed. Please use a different file.', 'error');
            return;
          }
        }

        console.log(`File selected for ${type}:`, displayAndStorageFileName);
        
        // Initialize upload state
        setUploadState(prev => ({
          ...prev,
          [type]: { 
            file: fileToUpload, 
            progress: true, 
            success: false, 
            error: null,
            fileName: displayAndStorageFileName, // Use the determined filename for display
            url: ''
          }
        }));

        // Show uploading message in Snackbar
        showSnackbar(`Uploading ${type} file...`, 'info');

        // Immediately upload the file
        try {
          // Pass the trimmed file object to handleFileUpload
          const uploadedUrl = await handleFileUpload(fileToUpload, type);
          console.log(`${type} file uploaded successfully:`, uploadedUrl);
          // Show success message in Snackbar
          showSnackbar(`${type} file uploaded successfully`, 'success');
        } catch (error) {
          console.error(`Error uploading ${type} file:`, error);
          setUploadState(prev => ({
            ...prev,
            [type]: { 
              ...prev[type],
              progress: false,
              error: 'Error uploading file',
              success: false
            }
          }));
          // Show error message in Snackbar
          showSnackbar(`Error uploading ${type} file`, 'error');
        }
      }
    } catch (error) {
      console.error(`Error in handleFileSelect for ${type}:`, error);
      setUploadState(prev => ({
        ...prev,
        [type]: { 
          ...prev[type],
          error: 'Error selecting file',
          success: false
        }
      }));
      showSnackbar('Error selecting file', 'error');
    }
  };

  const handleFileUpload = async (
    file: File,
    type: 'audio' | 'image',
    isEditing: boolean = false
  ): Promise<string> => {
    if (!file) return '';

    try {
      // Validate file type
      if (type === 'audio' && !file.type.startsWith('audio/')) {
        showSnackbar('Please upload a valid audio file', 'error');
        return '';
      }
      if (type === 'image' && !file.type.startsWith('image/')) {
        showSnackbar('Please upload a valid image file', 'error');
        return '';
      }

      // File size validation removed - allowing any size

      // Determine filename to use for storage path - trim if over 50 chars
      const baseFileName = file.name.length > 50 ? file.name.substring(0, 50) : file.name;

      setUploadState((prev: UploadState) => ({
        ...prev,
        [type]: { ...prev[type], progress: true, error: null }
      }));

      const fileExtension = file.name.split('.').pop();
      let fileName;
      if (type === 'image') {
        fileName = `Yantra_Images/${baseFileName}`;
      } else {
        fileName = `Audio_Files/${baseFileName}`;
      }
      const storageRef = ref(storage, fileName);
      const metadata = { contentType: file.type };

      if (type === 'audio') {
        // Use resumable upload for audio to track progress
        return await new Promise<string>((resolve, reject) => {
          const uploadTask = uploadBytesResumable(storageRef, file, metadata);
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadState(prev => ({
                ...prev,
                audio: { ...prev.audio, progress: true, success: false, error: null, fileName: file.name, url: '', percent: progress }
              }));
            },
            (error) => {
              setUploadState(prev => ({
                ...prev,
                audio: { ...prev.audio, progress: false, success: false, error: 'Upload failed', fileName: file.name, url: '' }
              }));
              showSnackbar('Error uploading audio file', 'error');
              reject(error);
            },
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              const encodedPath = downloadURL.split('/o/')[1];
              setUploadState(prev => ({
                ...prev,
                audio: { ...prev.audio, progress: false, success: true, error: null, fileName: file.name, url: downloadURL, percent: 100 }
              }));
              resolve(encodedPath);
            }
          );
        });
      } else {
        // For image, compress and upload both original and thumbnail
        try {
          // Compress the image before upload
          const compressedFile = await compressImage(file);
          
          // Upload compressed image
          await uploadBytes(storageRef, compressedFile, metadata);
          const downloadURL = await getDownloadURL(storageRef);
          const encodedPath = downloadURL.split('/o/')[1];

          // Generate and upload thumbnail
          const thumbnailBlob = await createThumbnail(compressedFile);
          const thumbnailFileName = `thumbnails/${baseFileName}`;
          const thumbnailRef = ref(storage, thumbnailFileName);
          await uploadBytes(thumbnailRef, thumbnailBlob, { contentType: 'image/jpeg' });
          const thumbnailUrl = await getDownloadURL(thumbnailRef);
          const encodedThumbnailPath = thumbnailUrl.split('/o/')[1];

          setUploadState((prev: UploadState) => ({
            ...prev,
            image: { 
              ...prev.image, 
              progress: false, 
              success: true, 
              fileName: file.name, 
              url: downloadURL,
              thumbnailUrl: thumbnailUrl
            }
          }));

          return encodedPath;
        } catch (error) {
          console.error('Error uploading image or thumbnail:', error);
          showSnackbar('Error uploading image or thumbnail', 'error');
          throw error;
        }
      }
    } catch (error) {
      setUploadState((prev: UploadState) => ({
        ...prev,
        [type]: { ...prev[type], progress: false, error: 'Upload failed', success: false, url: '' }
      }));
      showSnackbar('Error uploading file', 'error');
      throw error;
    }
  };

  const resetForm = () => {
    setCardName('');
    setCardDescription('');
    setPricingType('Free');
    setPrice('');
    setUploadState({
      audio: { 
        file: null, 
        progress: false, 
        success: false, 
        error: null, 
        fileName: '',
        url: ''
      },
      image: { 
        file: null, 
        progress: false, 
        success: false, 
        error: null, 
        fileName: '',
        url: ''
      }
    });
    if (audioInputRef.current) audioInputRef.current.value = '';
    if (imageInputRef.current) imageInputRef.current.value = '';
    setEditMode(false);
    setEditCardId('');
    setOriginalCardData(null);
    setHasChanges(false);
    setFormErrors({
      cardName: '',
      cardDescription: '',
      price: '',
      image_url: '',
      audio_url: ''
    });
    // Close the collapsible section
    setIsFormExpanded(false);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const validateForm = () => {
    const newErrors = {
      cardName: '',
      cardDescription: '',
      price: '',
      image_url: '',
      audio_url: ''
    };

    let isValid = true;

    // Validate card name
    if (!cardName.trim()) {
      newErrors.cardName = 'Card name is required';
      isValid = false;
    } else if (cardName.length > 50) {
      newErrors.cardName = 'Card name cannot exceed 50 characters';
      isValid = false;
    }

    // Validate card description
    if (!cardDescription.trim()) {
      newErrors.cardDescription = 'Card description is required';
      isValid = false;
    }

    // Validate price for paid cards
    if (pricingType === 'Paid') {
      if (!price) {
        newErrors.price = 'Price is required for paid cards';
        isValid = false;
      } else if (parseFloat(price) <= 0) {
        newErrors.price = 'Price must be greater than 0';
        isValid = false;
      }
    }

    // Validate required files - now required for both new cards and editing
    if (!uploadState.image.file && !uploadState.image.url) {
      newErrors.image_url = 'Image is required';
      isValid = false;
    }
    if (!uploadState.audio.file && !uploadState.audio.url) {
      newErrors.audio_url = 'Audio description is required';
      isValid = false;
    }

    // Set all errors at once
    setFormErrors(newErrors);

    // Focus on the first invalid field
    if (!isValid) {
      if (newErrors.cardName) {
        cardNameRef.current?.focus();
      } else if (newErrors.cardDescription) {
        cardDescriptionRef.current?.focus();
      } else if (newErrors.price) {
        priceRef.current?.focus();
      }
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent | React.MouseEvent) => {
    console.log('handleSubmit called');

    if (e) {
      e.preventDefault();
    }

    // Validate form
    const trimmedCardName = cardName.trim();
    const trimmedDescription = cardDescription.trim();
    console.log('Form values:', { trimmedCardName, trimmedDescription, pricingType, price });

    const newErrors: FormErrors = {
      cardName: '',
      cardDescription: '',
      price: '',
      image_url: '',
      audio_url: ''
    };

    let hasErrors = false;

    if (!trimmedCardName) {
      newErrors.cardName = 'Card name is required';
      hasErrors = true;
    }
    if (!trimmedDescription) {
      newErrors.cardDescription = 'Description is required';
      hasErrors = true;
    }
    if (pricingType === 'Paid' && (!price || parseFloat(price) <= 0)) {
      newErrors.price = 'Please enter a valid price';
      hasErrors = true;
    }

    // Duplicate card name check (normalized, exclude current card in edit mode)
    const normalizedInput = normalizeCardName(trimmedCardName);
    const isDuplicate = cards.some(card =>
      normalizeCardName(card.name) === normalizedInput &&
      card.id !== editCardId
    );
    if (isDuplicate) {
      newErrors.cardName = 'A card with this name already exists.';
      setFormErrors(newErrors);
      showSnackbar('A card with this name already exists.', 'error');
      return;
    }

    // Check for required files only if not in edit mode or if new files are being uploaded
    if (!editMode) {
      if (!uploadState.image.file && !uploadState.image.url) {
        newErrors.image_url = 'Image is required';
        hasErrors = true;
      }
      if (!uploadState.audio.file && !uploadState.audio.url) {
        newErrors.audio_url = 'Audio is required';
        hasErrors = true;
      }
    } else {
      // In edit mode, only require files if they're not already present
      if (!uploadState.image.file && !uploadState.image.url) {
        newErrors.image_url = 'Image is required';
        hasErrors = true;
      }
      if (!uploadState.audio.file && !uploadState.audio.url) {
        newErrors.audio_url = 'Audio is required';
        hasErrors = true;
      }
    }

    console.log('Validation errors:', newErrors);
    setFormErrors(newErrors);

    if (hasErrors) {
      console.log('Form validation failed');
      return;
    }

    try {
      console.log('Starting save process');
      setLoading(true);

      // Handle file uploads first
      let audioUrl = '';
      let imageUrl = '';
      let thumbnailUrl = '';

      // Only upload audio if it's a new file and hasn't been uploaded yet
      if (uploadState.audio.file && !uploadState.audio.success) {
        console.log('Uploading audio file');
        try {
          const uploadedAudioUrl = await handleFileUpload(uploadState.audio.file, 'audio');
          if (uploadedAudioUrl) {
            audioUrl = uploadedAudioUrl;
            console.log('Audio uploaded successfully:', audioUrl);
          } else {
            showSnackbar('Audio file upload failed', 'error');
            return;
          }
        } catch (error) {
          console.error('Error uploading audio file:', error);
          showSnackbar('Error uploading audio file', 'error');
          return;
        }
      } else if (uploadState.audio.success && uploadState.audio.url) {
        // Use the already uploaded audio URL
        audioUrl = uploadState.audio.url.split('/o/')[1];
        console.log('Using existing audio URL:', audioUrl);
      }

      // Only upload image if it's a new file and hasn't been uploaded yet
      if (uploadState.image.file && !uploadState.image.success) {
        console.log('Uploading image file');
        try {
          const uploadedImageUrl = await handleFileUpload(uploadState.image.file, 'image');
          if (uploadedImageUrl) {
            imageUrl = uploadedImageUrl;
            thumbnailUrl = uploadState.image.thumbnailUrl?.split('/o/')[1] || '';
            console.log('Image uploaded successfully:', imageUrl);
            console.log('Thumbnail uploaded successfully:', thumbnailUrl);
          } else {
            showSnackbar('Image file upload failed', 'error');
            return;
          }
        } catch (error) {
          console.error('Error uploading image file:', error);
          showSnackbar('Error uploading image file', 'error');
          return;
        }
      } else if (uploadState.image.success && uploadState.image.url) {
        // Use the already uploaded image URL
        imageUrl = uploadState.image.url.split('/o/')[1];
        thumbnailUrl = uploadState.image.thumbnailUrl?.split('/o/')[1] || '';
        console.log('Using existing image URL:', imageUrl);
        console.log('Using existing thumbnail URL:', thumbnailUrl);
      }

      // Preserve original values if not changed in edit mode
      if (editMode && originalCardData) {
        if (!imageUrl) imageUrl = originalCardData.image_url;
        if (!thumbnailUrl) thumbnailUrl = originalCardData.thumbnail_url || '';
        if (!audioUrl) audioUrl = originalCardData.audio_url;
      }

      // Prepare card data with proper date handling using Firestore Timestamp
      const currentTimestamp = Timestamp.now();
      console.log('Current timestamp:', currentTimestamp);

      const cardData = {
        name: trimmedCardName,
        description: trimmedDescription,
        is_free: pricingType === 'Free',
        price: pricingType === 'Paid' ? parseFloat(price) : 0,
        updated_at: currentTimestamp,
        image_url: imageUrl,
        thumbnail_url: thumbnailUrl,
        audio_url: audioUrl
      };

      console.log('Prepared card data:', cardData);

      let cardId: string;

      if (editMode && editCardId) {
        console.log('Updating existing card:', editCardId);
        // Update existing card
        const existingCard = cards.find(c => c.id === editCardId);
        if (!existingCard) {
          showSnackbar('Card not found', 'error');
          return;
        }

        // Handle the created_at date properly
        let created_at: Timestamp;
        const existingCreatedAt = existingCard.created_at;
        if (existingCreatedAt && typeof existingCreatedAt === 'object' && 'toDate' in existingCreatedAt) {
          created_at = existingCreatedAt as Timestamp;
        } else if (existingCreatedAt && typeof existingCreatedAt === 'object' && 'getTime' in existingCreatedAt) {
          created_at = Timestamp.fromDate(existingCreatedAt as Date);
        } else {
          created_at = Timestamp.fromDate(new Date(existingCreatedAt));
        }

        const updateData = {
          ...cardData,
          product_id: existingCard.product_id,
          created_at: created_at,
          is_active: existingCard.is_active
        };

        console.log('Update data:', updateData);

        const cardRef = doc(db, 'cards', editCardId);
        await updateDoc(cardRef, updateData);
        cardId = editCardId;
        console.log('Card updated successfully');
        showSnackbar('Card updated successfully', 'success');
      } else {
        console.log('Adding new card');
        // Add new card
        const nextProductId = await getNextProductId();
        const newCardData = {
          ...cardData,
          product_id: nextProductId,
          created_at: currentTimestamp,
          is_active: true
        };

        console.log('New card data:', newCardData);

        const docRef = await addDoc(collection(db, 'cards'), newCardData);
        cardId = docRef.id;
        console.log('New card added successfully with ID:', cardId);
        showSnackbar('Card added successfully', 'success');
      }

      // Refresh the cards list
      console.log('Refreshing cards list');
      await fetchCards();

      // Set the highlighted card ID
      setHighlightedCardId(cardId);

      // Reset form
      console.log('Resetting form');
      resetForm();
    } catch (error) {
      console.error('Error saving card:', error);
      showSnackbar('Error saving card', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (card: CardData) => {
    setEditMode(true);
    setEditCardId(card.id);
    setCardName(card.name);
    setCardDescription(card.description);
    setPricingType(card.is_free ? 'Free' : 'Paid');
    if (!card.is_free && card.price) {
      setPrice(card.price.toString());
    }
    
    // Store original card data for change detection
    setOriginalCardData(card);
    setHasChanges(false);
    
    setUploadState({
      audio: { 
        file: null, 
        progress: false, 
        success: !!card.audio_url, 
        error: null,
        fileName: card.audio_url ? card.audio_url.split('?')[0].split('%2F').pop()?.split('/').pop() || '' : '',
        url: card.audio_url ? `${FIREBASE_STORAGE_BASE_URL}${card.audio_url}` : ''
      },
      image: { 
        file: null, 
        progress: false, 
        success: !!card.image_url, 
        error: null,
        fileName: card.image_url ? card.image_url.split('?')[0].split('%2F').pop()?.split('/').pop() || '' : '',
        url: card.image_url ? `${FIREBASE_STORAGE_BASE_URL}${card.image_url}` : '',
        thumbnailUrl: card.thumbnail_url ? `${FIREBASE_STORAGE_BASE_URL}${card.thumbnail_url}` : ''
      }
    });

    setFormErrors({
      cardName: '',
      cardDescription: '',
      price: '',
      image_url: '',
      audio_url: ''
    })

    // Expand the form when editing
    setIsFormExpanded(true);

    // Scroll to top of the form
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleStatusToggle = async (cardId: string, newStatus: boolean) => {
    const card = cards.find(c => c.id === cardId);
    if (!card) return;

    // If trying to deactivate a paid card, show confirmation dialog
    if (!newStatus && !card.is_free) {
      setCardToToggle({ id: cardId, currentStatus: newStatus });
      setStatusDialogOpen(true);
      return;
    }

    // For free cards or activating paid cards, proceed directly
    await updateCardStatus(cardId, newStatus);
  };

  const updateCardStatus = async (cardId: string, newStatus: boolean) => {
    try {
      await updateDoc(doc(db, 'cards', cardId), {
        is_active: newStatus
      });
      showSnackbar('Card status updated successfully', 'success');
      fetchCards();
    } catch (error) {
      console.error('Error updating card status:', error);
      showSnackbar('Error updating card status', 'error');
    } finally {
      setStatusDialogOpen(false);
      setCardToToggle(null);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Add getSortedCards function after all state declarations
  const getSortedCards = (cards: CardData[]) => {
    return [...cards].sort((a, b) => {
      let comparison = 0;
      
      switch (orderBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'pricing':
          // Sort by is_free first, then by price
          if (a.is_free !== b.is_free) {
            comparison = a.is_free ? -1 : 1;
          } else {
            comparison = (a.price || 0) - (b.price || 0);
          }
          break;
        case 'updated_at':
          // Convert Timestamp to milliseconds for comparison
          const aTime = a.updated_at instanceof Timestamp ? a.updated_at.toMillis() : new Date(a.updated_at).getTime();
          const bTime = b.updated_at instanceof Timestamp ? b.updated_at.toMillis() : new Date(b.updated_at).getTime();
          comparison = aTime - bTime;
          break;
        default:
          comparison = 0;
      }
      
      return order === 'asc' ? comparison : -comparison;
    });
  };

  // Update handleRequestSort function
  const handleRequestSort = (property: SortField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Update the filtered and sorted cards logic
  const filteredCards = cards.filter(card => 
    card.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedCards = getSortedCards(filteredCards);

  // Update pagination to use sortedCards
  const paginatedCards = sortedCards.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Update handleCardNameChange
  const handleCardNameChange = (value: string) => {
    const newErrors = { ...formErrors };
    let truncatedValue = value;
    let showLengthError = false;

    if (value.length > 50) {
      truncatedValue = value.slice(0, 50);
      showLengthError = true;
    }

    const normalizedInput = normalizeCardName(truncatedValue);

    if (truncatedValue.startsWith(' ')) {
      newErrors.cardName = 'Card name cannot start with a space';
    } else if (truncatedValue.endsWith(' ')) {
      newErrors.cardName = 'Card name cannot end with a space';
    } else if (!normalizedInput) {
      newErrors.cardName = 'Card name is required';
    } else if (showLengthError) {
      newErrors.cardName = 'Card name cannot exceed 50 characters';
    } else {
      // Check for duplicate card name (normalized)
      const isDuplicate = cards.some(card => 
        normalizeCardName(card.name) === normalizedInput && 
        card.id !== editCardId
      );
      if (isDuplicate) {
        newErrors.cardName = 'A card with this name already exists.';
      } else {
        newErrors.cardName = '';
      }
    }
    setFormErrors(newErrors);
    setCardName(truncatedValue);
  };

  const handleCardDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardDescription(e.target.value);
    // Clear error when user starts typing
    if (formErrors.cardDescription) {
      setFormErrors(prev => ({ ...prev, cardDescription: '' }));
    }
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const newErrors = { ...formErrors };
    
    // Only allow numbers and a single decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      // Prevent multiple decimal points
      if ((value.match(/\./g) || []).length <= 1) {
        // Limit to 2 decimal places
        if (value.includes('.')) {
          const decimalPlaces = value.split('.')[1];
          if (decimalPlaces.length <= 2) {
            // Check if the value is less than or equal to 9999.99
            const numericValue = parseFloat(value);
            if (isNaN(numericValue) || numericValue <= 9999.99) {
              setPrice(value);
              if (formErrors.price) {
                setFormErrors({ ...formErrors, price: '' });
              }
            } else {
              newErrors.price = 'Price cannot exceed $9999.99';
              setFormErrors(newErrors);
            }
          }
        } else {
          // Check if the value is less than or equal to 9999.99
          const numericValue = parseFloat(value);
          if (isNaN(numericValue) || numericValue <= 9999.99) {
            setPrice(value);
            if (formErrors.price) {
              setFormErrors({ ...formErrors, price: '' });
            }
          } else {
            newErrors.price = 'Price cannot exceed $9999.99';
            setFormErrors(newErrors);
          }
        }
      }
    }
  };

  const handleViewImage = (imageUrl: string) => {
    // Construct the full Firebase Storage URL by adding base URL to the stored path
    const fullImageUrl = `${FIREBASE_STORAGE_BASE_URL}${imageUrl}`;
    console.log('Image URL:', fullImageUrl); // Debug log
    setPreviewImage(fullImageUrl);
    setPreviewDialogOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
    setPreviewImage(null);
  };

  // Add function to handle audio play
  const handleAudioPlay = (cardId: string) => {
    // Pause any currently playing audio
    if (currentlyPlayingId && currentlyPlayingId !== cardId) {
      const previousAudio = audioRefs.current[currentlyPlayingId];
      if (previousAudio) {
        previousAudio.pause();
      }
    }
    setCurrentlyPlayingId(cardId);
  };

  // Add function to handle audio pause
  const handleAudioPause = (cardId: string) => {
    if (currentlyPlayingId === cardId) {
      setCurrentlyPlayingId(null);
    }
  };

  const handlePricingTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPricingType = e.target.value;
    setPricingType(newPricingType);
    
    // Clear price validation error when switching to Free
    if (newPricingType === 'Free') {
      setFormErrors(prev => ({ ...prev, price: '' }));
      setPrice(''); // Also clear the price value
    }
  };

  const createThumbnail = async (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      
      img.onload = () => {
        try {
          // Create canvas for thumbnail
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            throw new Error('Failed to get canvas context');
          }

          // Calculate thumbnail dimensions while maintaining aspect ratio
          const maxSize = 200; // Maximum dimension for thumbnail
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxSize) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }

          // Set canvas dimensions
          canvas.width = width;
          canvas.height = height;

          // Draw image with high-quality settings
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with quality settings
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to create thumbnail blob'));
              }
            },
            'image/jpeg',
            0.85 // Quality setting (0.85 = 85% quality)
          );

          // Clean up
          URL.revokeObjectURL(img.src);
        } catch (error) {
          URL.revokeObjectURL(img.src);
          reject(error);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Failed to load image for thumbnail creation'));
      };
    });
  };

  return (
      <MainContent>
      {/* Add Snackbar component at the top level */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            minWidth: 'auto',
            backgroundColor: '#FF7043',
            color: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          '& .MuiAlert-root': {
            width: '100%',
            marginTop: '16px'
          }
        }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            '& .MuiAlert-message': {
              color: '#000000'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          onClick={() => setIsFormExpanded(!isFormExpanded)}
          sx={{
            color: '#424242',
            textTransform: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            justifyContent: 'flex-start',
            padding: '4px 12px',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            backgroundColor: '#ffd999',
            '&:hover': {
              backgroundColor: '#ffd999'
            }
          }}
        >
          {isFormExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          <Typography variant="h6" sx={{ color: '#424242', fontWeight: 'bold', fontSize: '1.5rem' }}>
            {editMode ? 'Edit Card' : 'Add New Card'}
          </Typography>
        </Button>
      </Box>

      <Collapse in={isFormExpanded}>
        <FormContainer ref={formRef}>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            {/* Form Content Container */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, width: '100%' }}>
              {/* Left Section - Input Fields */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Typography sx={{ minWidth: '120px', fontSize: '14px', mt: 1 }}>Card Name :</Typography>
              <TextField
                required
                fullWidth
                value={cardName}
                size="small"
                    error={!!formErrors.cardName}
                    helperText={formErrors.cardName}
                    onChange={(e) => handleCardNameChange(e.target.value)}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white'
                      }
                    }}
              />
            </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Typography sx={{ minWidth: '120px', fontSize: '14px', mt: 1 }}>Card Description :</Typography>
              <TextField
                required
                fullWidth
                multiline
                rows={4}
                value={cardDescription}
                    error={!!formErrors.cardDescription}
                    helperText={formErrors.cardDescription}
                    onChange={handleCardDescriptionChange}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white'
                      }
                    }}
              />
            </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Typography sx={{ minWidth: '120px', fontSize: '14px', mt: 1 }}>Pricing :</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <RadioGroup
                      value={pricingType}
                      onChange={handlePricingTypeChange}
                    >
                      <FormControlLabel 
                        value="Free" 
                        control={<Radio />} 
                        label="Free" 
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControlLabel 
                          value="Paid" 
                          control={<Radio />} 
                          label="Paid" 
                          sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
                        />
                        {pricingType === 'Paid' && (
                          <TextField
                            required
                            size="small"
                            value={price}
                            error={!!formErrors.price}
                            onChange={handlePriceChange}
                            sx={{ width: '168px',
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white'
                              }
                            }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                          />
                        )}
                      </Box>
                    </RadioGroup>
                    {pricingType === 'Paid' && formErrors.price && (
                      <Typography sx={{ 
                        fontSize: '12px', 
                        color: '#f44336',
                       marginLeft: '92px',
                    textAlign: 'left'
                      }}>
                        {formErrors.price}
                      </Typography>
                    )}
                  </Box>
                </Box>
            </Box>

              {/* Right Section - File Uploads */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                {/* Upload Buttons Box */}
                <Box sx={{ 
                  border: '1px solid #000000', 
                  borderRadius: '4px', 
                  p: 2, 
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}>
                  {/* Upload Files Caption */}
                  <Typography sx={{ 
                    fontSize: '14px', 
                    color: '#424242', 
                    position: 'absolute',
                    top: -10,
                    left: 10,
                    backgroundColor: '#ffd999',
                    px: 1
                  }}>
                    Upload Files
                  </Typography>

                  {/* Content Container */}
                  <Box sx={{ 
                    display: 'flex',
                    width: '100%',
                    gap: 4,
                    justifyContent: 'center'
                  }}>
                    {/* Left Section - Audio */}
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      flex: 1
                    }}>
                      {/* Audio Upload Button */}
              <input
                type="file"
                id="audio-upload"
                accept="audio/mp3,audio/*,.mp3,.wav,.m4a,.ogg"
                onChange={(e) => handleFileSelect(e, 'audio')}
                style={{ display: 'none' }}
                ref={audioInputRef}
              />
              <Button
                        component="label"
                        htmlFor="audio-upload"
                        sx={{
                          backgroundColor: '#2196F3',
                          color: 'white',
                          width: '180px',
                          height: '32px',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: '#1976D2',
                          },
                        }}
                      >
                        Audio description
              </Button>
                      {/* Audio Messages */}
                      <Box sx={{ width: '180px', minHeight: '20px', textAlign: 'center' }}>
                        {uploadState.audio.success && uploadState.audio.url && (
                          <>
                            <Typography sx={{
                              fontSize: '12px',
                              color: '#4CAF50',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              justifyContent: 'center',
                              mb: 1
                            }}>
                              ✓ {uploadState.audio.fileName}
                            </Typography>
                            <CustomAudioPlayer
                              src={uploadState.audio.url}
                              isPlaying={currentlyPlayingId === 'form-upload'}
                              onPlay={() => setCurrentlyPlayingId('form-upload')}
                              onPause={() => setCurrentlyPlayingId(null)}
                              tooltipTitle={uploadState.audio.fileName}
                            />
                          </>
                        )}
                        {uploadState.audio.error && (
                          <Typography sx={{ 
                            fontSize: '12px', 
                            color: '#f44336',
                            textAlign: 'center'
                          }}>
                  {uploadState.audio.error}
                </Typography>
              )}
                        {formErrors.audio_url && (
                          <Typography sx={{ 
                            fontSize: '12px', 
                            color: '#f44336',
                            textAlign: 'center'
                          }}>
                            {formErrors.audio_url}
                </Typography>
              )}
                      </Box>
                      {uploadState.audio.progress && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, minWidth: 200 }}>
                          <LinearProgress variant="determinate" value={uploadState.audio.percent || 0} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                          <Typography sx={{ fontSize: 13, minWidth: 60 }}>{
                            uploadState.audio.percent && uploadState.audio.percent < 100
                              ? `Uploading: ${Math.round(uploadState.audio.percent)}%`
                              : uploadState.audio.fileName
                                ? `✓ ${uploadState.audio.fileName}`
                                : 'Upload complete'
                          }</Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Right Section - Image */}
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      flex: 1
                    }}>
                      {/* Image Upload Button */}
              <input
                type="file"
                id="image-upload"
                accept=".jpg,.jpeg,.png,.webp,.svg,.gif,image/jpeg,image/png,image/webp,image/svg+xml,image/gif"
                onChange={(e) => handleFileSelect(e, 'image')}
                style={{ display: 'none' }}
                ref={imageInputRef}
              />
              <Button
                        component="label"
                        htmlFor="image-upload"
                        sx={{
                          backgroundColor: '#2196F3',
                          color: 'white',
                          width: '180px',
                          height: '32px',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: '#1976D2',
                          },
                        }}
                      >
                        Image
              </Button>
                      {/* Image Messages */}
                      <Box sx={{ width: '180px', minHeight: '20px', textAlign: 'center' }}>
              {uploadState.image.success && (
                          <Typography sx={{ 
                            fontSize: '12px', 
                            color: '#4CAF50',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            justifyContent: 'center'
                          }}>
                            ✓ {uploadState.image.fileName}
                </Typography>
              )}
                        {formErrors.image_url && (
                          <Typography sx={{ 
                            fontSize: '12px', 
                            color: '#f44336',
                            textAlign: 'center'
                          }}>
                            {formErrors.image_url}
                </Typography>
              )}
            </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <AddButton
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || (editMode && !hasChanges)}
                sx={{
                  ...(editMode && !hasChanges && {
                    backgroundColor: '#2196F3',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#1976D2',
                    },
                    '&:disabled': {
                      backgroundColor: '#ccc',
                      color: '#666',
                      cursor: 'not-allowed'
                    },
                    textTransform: 'none',
                    width: '120px'
                    
                  })
                }}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : editMode ? (
                  'Update Card'
                ) : (
                  'Add New Card'
                )}
              </AddButton>
              {editMode ? (
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  sx={{ 
                    ml: 2,
                    backgroundColor: '#2196F3',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#1976D2',
                    }
                  }}
                >
                  Cancel
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  sx={{ 
                    ml: 2,
                    backgroundColor: '#2196F3',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#1976D2',
                    }
                  }}
                >
                  Reset
                </Button>
                )}
              </Box>
              {editMode && hasChanges && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#FF7043', 
                    fontSize: '12px',
                    fontStyle: 'italic'
                  }}
                >
                  Changes detected
                </Typography>
              )}
            </Box>
          </Box>
        </FormContainer>
      </Collapse>

        {/* Manage Cards Section */}
      <Box sx={{ mt: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: 2,
          mb: 2 
        }}>
          <Typography variant="h6" sx={{ color: '#424242', fontWeight: 'bold', fontSize: '1.3rem' }}>
            Manage Cards
          </Typography>
          <SearchTextField
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0); // Reset to first page when search query changes
            }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: '24px' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery('');
                      setPage(0); // Reset to first page when clearing search
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <CloseIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: '300px' }}
          />
        </Box>
          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className="status-cell">
                    Status 
                  </TableCell>
                  <TableCell className="name-cell">
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={orderBy === 'name' ? order : 'asc'}
                      onClick={() => handleRequestSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className="pricing-cell">
                    <TableSortLabel
                      active={orderBy === 'pricing'}
                      direction={orderBy === 'pricing' ? order : 'asc'}
                      onClick={() => handleRequestSort('pricing')}
                  >
                      Pricing
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className="image-cell">Image</TableCell>
                  <TableCell className="audio-cell">Audio</TableCell>
                  <TableCell className="actions-cell">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedCards.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No cards found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCards.map((card) => {
                    const isHighlighted = card.id === highlightedCardId;
                    const RowComponent = isHighlighted ? HighlightedRow : TableRow;
                    
                    return (
                      <RowComponent key={card.id}>
                        <TableCell className="status-cell">
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Switch
                              checked={card.is_active}
                              onChange={(e) => handleStatusToggle(card.id, e.target.checked)}
                            />
                          </Box>
                        </TableCell>
                        <TableCell className="name-cell" align="center" sx={{ textAlign: 'center' }}>
                          {card.name}
                        </TableCell>
                        <TableCell className="pricing-cell">
                          {card.is_free ? 'Free' : `$${card.price?.toFixed(2)}`}
                        </TableCell>
                        <TableCell className="image-cell" sx={{ textAlign: 'center' }}>
                          {card.image_url && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography
                                component="span"
                                sx={{
                                  color: 'primary.main',
                                  cursor: 'pointer !important',
                                  fontSize: '12px',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                                onClick={() => handleViewImage(card.thumbnail_url || card.image_url)}
                              >
                                View Image
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell className="audio-cell">
                          {card.audio_url && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                              <Tooltip 
                                title={`Play ${card.name}`}
                                placement="top"
                                arrow
                                sx={{
                                  '& .MuiTooltip-tooltip': {
                                    backgroundColor: '#FF7043',
                                    color: '#ffffff',
                                    fontSize: '14px',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }
                                }}
                              >
                                <CustomAudioPlayer
                                  src={editMode && editCardId === card.id ? uploadState.audio.url : `${FIREBASE_STORAGE_BASE_URL}${card.audio_url}`}
                                  isPlaying={currentlyPlayingId === card.id}
                                  onPlay={() => setCurrentlyPlayingId(card.id)}
                                  onPause={() => setCurrentlyPlayingId(null)}
                                  tooltipTitle={`Play ${card.name}`}
                                  showTimeline={false}
                                />
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell className="actions-cell">
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
                            <ActionButton
                              onClick={() => handleEdit(card)}
                              sx={{ 
                                color: '#FF7043',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              Edit
                            </ActionButton>
                          </Box>
                        </TableCell>
                      </RowComponent>
                    );
                  })
                )}
              </TableBody>
            </Table>
          <TablePagination
            component="div"
            count={sortedCards.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{
              '.MuiTablePagination-select': {
                fontSize: '14px',
                padding: '8px 16px',
              },
              '.MuiTablePagination-displayedRows': {
                fontSize: '14px',
              },
              '.MuiTablePagination-selectLabel': {
                fontSize: '14px',
              }
            }}
          />
          </StyledTableContainer>
        </Box>

      {/* Status Toggle Confirmation Dialog */}
        <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        >
        <DialogTitle>Confirm Deactivation</DialogTitle>
          <DialogContent>
          This card is a paid card. Are you sure you want to deactivate it?
          </DialogContent>
          <DialogActions>
            <Button
            onClick={() => setStatusDialogOpen(false)}
              sx={{ color: '#757575' }}
            >
              Cancel
            </Button>
            <Button
            onClick={() => cardToToggle && updateCardStatus(cardToToggle.id, cardToToggle.currentStatus)}
            variant="contained"
            sx={{ 
              backgroundColor: '#F44336',
              color: 'white',
              '&:hover': {
                backgroundColor: '#d32f2f'
              }
            }}
              autoFocus
            >
            Deactivate
            </Button>
          </DialogActions>
        </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={handleClosePreview}
        maxWidth="sm"
        fullWidth={false}
        PaperProps={{
          sx: {
            width: 400,
            height: 400,
            maxWidth: 400,
            maxHeight: 400,
            minWidth: 400,
            minHeight: 400,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', width: '100%' }}>Image Preview</DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            width: 400,
            height: 320,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
        >
          {previewImage && (
            <Box sx={{ width: 320, height: 320, display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#fafafa', borderRadius: 2, boxShadow: 1 }}>
              <img
                src={previewImage}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  margin: '0 auto',
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', width: '100%' }}>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>
      </MainContent>
  );
} 